import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Instagram } from 'lucide-react';
import { businessInfo } from '../data/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../data/mockData';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission - will be replaced with actual backend call
    setTimeout(() => {
      toast({
        title: "Quote Request Sent!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-hero-title">Get in Touch</h1>
          <p className="page-hero-subtitle">
            Ready to start your project? Contact us for a free consultation and quote
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="contact-form-title">Request a Free Quote</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+61 4XX XXX XXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="projectType">Project Type *</label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="pool-fencing">Glass Pool Fencing</option>
                    <option value="balustrade">Glass Balustrade</option>
                    <option value="aluminum-fencing">Aluminum Fencing & Louvers</option>
                    <option value="custom-panels">Custom Glass Panels</option>
                    <option value="shower-screens">Shower Screens</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us about your project, including dimensions, location, and any specific requirements..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <h2 className="contact-info-title">Contact Information</h2>
              
              <div className="contact-info-cards">
                <div className="contact-info-card">
                  <Phone className="contact-icon" />
                  <h3>Phone</h3>
                  <a href="tel:+61414443454">{businessInfo.phone}</a>
                  <p className="contact-note">Call us for immediate assistance</p>
                </div>

                <div className="contact-info-card">
                  <Mail className="contact-icon" />
                  <h3>Email</h3>
                  <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>
                  <p className="contact-note">Send us your inquiry anytime</p>
                </div>

                <div className="contact-info-card">
                  <MapPin className="contact-icon" />
                  <h3>Location</h3>
                  <p>{businessInfo.location}</p>
                  <p className="contact-note">Servicing all Sydney areas</p>
                </div>

                <div className="contact-info-card">
                  <Clock className="contact-icon" />
                  <h3>Business Hours</h3>
                  <p>{businessInfo.hours.weekdays}</p>
                  <p className="contact-note">{businessInfo.hours.sunday}</p>
                </div>

                <div className="contact-info-card">
                  <Instagram className="contact-icon" />
                  <h3>Instagram</h3>
                  <a href="https://www.instagram.com/genuine_fencers?igsh=MTJubnBzZ2NzZjFlMw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer">
                    @genuine_fencers
                  </a>
                  <p className="contact-note">Follow us for project updates</p>
                </div>
              </div>

              <div className="contact-cta-box">
                <h3>Prefer to Talk?</h3>
                <p>Give us a call for immediate assistance and expert advice on your glass fencing project.</p>
                <a href="tel:+61414443454" className="btn btn-secondary btn-full">
                  <Phone size={20} />
                  Call {businessInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-wrapper">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger>{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
